import {
  Box,
  Card,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import CustomTypography from "../form/CustomTypography";
import { observer } from "mobx-react";
import { AdvancePayment } from "../../../../models/Order/AdvancePayment";
import dayjs from "dayjs";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";

interface AdvancePaymentTableProps {
  data?: AdvancePayment[];
  padding?: string;
  boxShadow?: string;
  handleDeleteAdvancePayment: (id: number) => void;
  handleEditAdvancePayment?: (payment: AdvancePayment) => void;
}

const AdvancePaymentTable: React.FC<AdvancePaymentTableProps> = ({
  data,
  padding,
  boxShadow,
  handleDeleteAdvancePayment,
  handleEditAdvancePayment,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeRow, setActiveRow] = useState<AdvancePayment | null>(null);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    row: AdvancePayment,
  ) => {
    setMenuAnchor(event.currentTarget);
    setActiveRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setActiveRow(null);
  };

  const handleEdit = () => {
    if (activeRow && handleEditAdvancePayment) {
      handleEditAdvancePayment(activeRow);
    }
    handleCloseMenu();
  };

  const handleDelete = () => {
    if (activeRow) {
      handleDeleteAdvancePayment(Number(activeRow.id));
    }
    handleCloseMenu();
  };

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || 5,
          boxShadow:
            boxShadow ||
            "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700} sx={{ mb: 3 }}>
          Advance Payments
        </CustomTypography>

        {!data || data.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color="rgba(218, 218, 218, 1)"
            >
              NO PAYMENTS FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {data.map((row, index) => {
              const amountReceived =
                (Number(row.amount) || 0) -
                (row.bank_charges || 0) -
                (row.tds_charges || 0) -
                (row.other_charges || 0) -
                (row.forex_charges || 0);

              return (
                <Box
                  key={`${row.id}-${index}`}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.72rem",
                        color: "#94A3B8",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      #{row.id}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.72rem",
                        color: "#64748B",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Order #{row.order_id}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 80 }}>
                      <CustomTypography variant="body2" fontWeight={700}>
                        {row.payment_mode}
                      </CustomTypography>
                      {row.payment_reference_id && (
                        <CustomTypography variant="body3" color="#64748B">
                          Ref: {row.payment_reference_id}
                        </CustomTypography>
                      )}
                    </Box>
                    {row.recorded_date && (
                      <CustomTypography
                        variant="body3"
                        color="#94A3B8"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {dayjs(row.recorded_date).format("DD-MM-YYYY")}
                      </CustomTypography>
                    )}
                    {row.admin_name && (
                      <CustomTypography
                        variant="body3"
                        color="#64748B"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {row.admin_name}
                      </CustomTypography>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenMenu(e, row)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Metrics strip */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#FAFAFA",
                      borderTop: "1px solid #F1F5F9",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    {[
                      { label: "Total Amount", value: row.amount },
                      {
                        label: "Bank Charges",
                        value: row.bank_charges || "-",
                      },
                      { label: "TDS Charges", value: row.tds_charges || "-" },
                      {
                        label: "Other Charges",
                        value: row.other_charges || "-",
                      },
                      {
                        label:
                          row.forex_charges != null && row.forex_charges < 0
                            ? "Forex Gain"
                            : row.forex_charges != null && row.forex_charges > 0
                              ? "Forex Loss"
                              : "Forex Charges",
                        value:
                          row.forex_charges != null && row.forex_charges !== 0
                            ? row.forex_charges
                            : "-",
                      },
                      { label: "Amount Received", value: amountReceived },
                    ].map((m) => (
                      <Box
                        key={m.label}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          minWidth: 68,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.63rem",
                            color: "#94A3B8",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {m.label}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#334155",
                          }}
                        >
                          {m.value}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {handleEditAdvancePayment && (
            <MenuItem
              onClick={handleEdit}
              sx={{ gap: 0.75, py: 0.5, px: 1.5, minHeight: "unset" }}
            >
              <EditIcon
                sx={{ fontSize: 14, color: theme.palette.primary.main }}
              />
              <span style={{ fontSize: "0.75rem" }}>Edit</span>
            </MenuItem>
          )}
          <MenuItem
            onClick={handleDelete}
            sx={{ gap: 0.75, py: 0.5, px: 1.5, minHeight: "unset" }}
          >
            <DeleteIcon
              sx={{ fontSize: 14, color: theme.palette.error.main }}
            />
            <span style={{ fontSize: "0.75rem" }}>Delete</span>
          </MenuItem>
        </Menu>
      </Card>
    </Box>
  );
};

export default observer(AdvancePaymentTable);
